import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { Modal, ModalBody, ModalHeader } from '@change/ds-core-ui/Modal';
import { Text } from '@change/ds-core-ui/Text';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { useRestoreFocusOnClose } from '#src/app/shared/hooks/focus';

import type { PromoteSlideUpProps } from './types';

/** Its presence, not its behaviour, makes the DS render the close icon; it must stay a no-op or dismissal double-fires. */
const noop = () => {};

export const PromoteSlideUp: React.FC<PromoteSlideUpProps> = ({
	isOpen,
	onOpenChange,
	amountLocalized,
	onChipIn,
	onSkip,
	restoreFocusRef,
}) => {
	const { translate } = useI18n();
	useRestoreFocusOnClose(isOpen, restoreFocusRef);

	return (
		<Modal
			isOpen={isOpen}
			transformToSheet
			onClose={noop}
			onOpenChange={onOpenChange}
			// eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- key comes from the revenue_share_promote_slide_up_experiment FC's copy via I18nOverrides
			aria-label={translate('corgi.psf.share.promote_slide_up.heading')}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			className="z-100"
		>
			{/* eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- key comes from the revenue_share_promote_slide_up_experiment FC's copy via I18nOverrides */}
			<ModalHeader title={translate('corgi.psf.share.promote_slide_up.heading')} />

			<ModalBody className="pb-6" data-testid="promote-slide-up">
				<div className="flex w-full flex-col gap-5">
					<Text as="p" variant="body" size="medium">
						{/* eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- key comes from the revenue_share_promote_slide_up_experiment FC's copy via I18nOverrides */}
						<Translate value="corgi.psf.share.promote_slide_up.body" />
					</Text>

					<div className="flex flex-col gap-3">
						<Button data-qa="promote-slide-up-chip-in-button" variant="primary" className="w-full" onClick={onChipIn}>
							<Translate
								// eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- key comes from the revenue_share_promote_slide_up_experiment FC's copy via I18nOverrides
								value="corgi.psf.share.promote_slide_up.chip_in_cta"
								replacements={{ amount: amountLocalized }}
							/>
						</Button>

						<Button data-qa="promote-slide-up-skip-button" variant="tertiary" className="w-full" onClick={onSkip}>
							{/* eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- key comes from the revenue_share_promote_slide_up_experiment FC's copy via I18nOverrides */}
							<Translate value="corgi.psf.share.promote_slide_up.skip" />
						</Button>
					</div>
				</div>
			</ModalBody>
		</Modal>
	);
};
